<script setup lang="ts">
import { Customer, Order } from '@commercetools/platform-sdk';
import { dollars } from '@nuts/auto-delivery-sdk/dist/utils/money';
import { useCurrentElement, useEventListener, whenever } from '@vueuse/core';
import { computed, onMounted, reactive, ref, watch, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import InformationBanner from '@/components/base/layout/InformationBanner.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import CustomerEmailSummary from '@/components/checkout/cxMode/CustomerEmailSummary.vue';
import SearchCustomerByEmail from '@/components/checkout/cxMode/SearchCustomerByEmail.vue';
import Step from '@/components/checkout/Step.vue';
import Address from '@/components/checkout/steps/address/Address.vue';
import AddressSummary from '@/components/checkout/steps/address/AddressSummary.vue';
import Delivery from '@/components/checkout/steps/delivery/Delivery.vue';
import DeliverySummary from '@/components/checkout/steps/delivery/DeliverySummary.vue';
import Payment, { PaymentStepProps } from '@/components/checkout/steps/payment/Payment.vue';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import { BaseStep, useCheckout } from '@/composables/useCheckout';
import { useDelivery } from '@/composables/useDelivery';
import { useDiscount } from '@/composables/useDiscount';
import { usePayment } from '@/composables/usePayment';
import { USBankAccountStatus } from '@/composables/useWallet';
import { containsGiftPurchase, NutsLineItem } from '@/lib/cart/lineItem';
import { useCustomer } from '@/stores/customer';
import { formatPurchaseItem, gtag } from '@/utils/analytics';
import {
  NutsCheckoutStepCompleted,
  sendCheckoutStepCompletedEvent,
} from '@/utils/analytics/checkoutStepCompletedEvent';
import { getCartId, needPhysicalAddress as needAddress } from '@/utils/cart';

export interface ExtendedStep extends BaseStep {
  route: { step: string; stepVariant?: string };
  /** Starts at 1 */
  stepNumber: number;
}

defineOptions({
  components: {
    Address,
    AddressSummary,
    Delivery,
    DeliverySummary,
    Payment,
  },
});

const props = withDefaults(
  defineProps<{
    currentStep: 'signin' | 'address' | 'shipping' | 'payment' | '';
    customer?: Customer | null;
    lineItems: NutsLineItem[];
    oneClickCheckout?: boolean;
    stepVariant?: string;
  }>(),
  {
    currentStep: '',
    customer: null,
    stepVariant: '',
  },
);

const router = useRouter();
const store = useStore();
const { cart, cartCopiedFromOrder, unassignedLineItems } = useCart(store);
const { cxMode, steps: dynamicSteps } = useCheckout(store);
const customerStore = useCustomer();
const delivery = useDelivery(store);
const {
  calculateShippingOffers,
  futureDeliveryDates,
  hasMultipleShipments,
  isPickup,
  saveDeliveryOptions,
  setShippingOfferSets,
  shippingOfferSets,
  specialDeliveryShipments,
  standardShipments,
} = delivery;
const { appliedDiscountCodes } = useDiscount(store);
const { determineAvailablePaymentMethods, estimatedAmountToCharge, getSavedPaymentMethods } =
  usePayment(store, cxMode);

const awaitingCustomer = computed(() => props.currentStep === 'signin');
const oneClickCheckoutEligible = computed(
  () =>
    props.oneClickCheckout &&
    !isPickup.value &&
    standardShipments.value.length > 0 &&
    unassignedLineItems.value.length === 0,
);

const availablePaymentMethods = ref<PaymentStepProps['availablePaymentMethods']>();
const paymentMethods = ref<PaymentStepProps['paymentMethods']>();
const paymentUnlocked = ref(props.currentStep === 'payment');

const lasershipAllowed = ref<boolean>(customerStore.customerLasershipAllowed);
const ontracAllowed = ref<boolean>(customerStore.customerOntracAllowed);
const regionalCarriersAllowed = ref<boolean>(customerStore.customerRegionalCarriersAllowed);

const root = useCurrentElement<HTMLElement>();
const loadShippingOffers = useCallback(async () => {
  const { offerSets, futureDeliveryDates: dates } = await calculateShippingOffers({
    cxMode,
    lasershipAllowed: lasershipAllowed.value,
    ontracAllowed: ontracAllowed.value,
    regionalCarriersAllowed: regionalCarriersAllowed.value,
  });
  setShippingOfferSets(offerSets);
  futureDeliveryDates.value = dates;
  await saveDeliveryOptions();
});

const disablePaymentStep = computed(() => paymentUnlocked.value && props.currentStep === 'address');

const steps = computed<Partial<Record<ExtendedStep['key'], ExtendedStep>>>(() =>
  Object.fromEntries(
    dynamicSteps.value.map<[string, ExtendedStep]>((step, index) => {
      const route: { step: string; stepVariant?: string } = {
        step: step.key.replace('delivery', 'shipping'),
      };
      if (step.key === 'address' && hasMultipleShipments.value) {
        route.stepVariant = 'multi';
      }
      return [
        step.key,
        {
          ...step,
          // for now, unlock the step manually; must be enabled to unlock
          enabled: step.key === 'payment' ? paymentUnlocked.value : step.enabled,
          route,
          stepNumber: index + 1,
        },
      ];
    }),
  ),
);

const getCheckoutType = () => {
  if (cxMode.value) return 'CX';
  return props.customer ? 'Customer' : 'Guest';
};
const getCheckoutEvent = () => ({
  checkout_type: getCheckoutType(),
  coupon: appliedDiscountCodes.value?.[0]?.code,
  currency: 'USD',
  items: props.lineItems.map(formatPurchaseItem),
  value: estimatedAmountToCharge.value,
});
const onOrderPlaced = (receiptToken: string, order: Order) => {
  const checkoutEvent = getCheckoutEvent();
  gtag('event', 'add_payment_info', {
    ...checkoutEvent,
    payment_type: order?.paymentInfo?.payments[0]?.obj?.paymentMethodInfo?.name?.en,
    value: dollars(checkoutEvent.value),
  });
  router.push({ name: 'CheckoutReceipt', query: { token: receiptToken } });
};
const onStepComplete = (completedStep: string) => {
  const nonPaymentStep = (step: string): step is NutsCheckoutStepCompleted['step'] =>
    ['address', 'delivery'].includes(step);

  if (nonPaymentStep(completedStep)) {
    const checkoutEvent = getCheckoutEvent();
    sendCheckoutStepCompletedEvent({
      checkout_id: getCartId(cart.value),
      checkoutType: checkoutEvent.checkout_type,
      coupon: checkoutEvent.coupon,
      lineItems: props.lineItems,
      shipping_option: standardShipments.value[0]?.customFields?.shipmentPickupShippingOption,
      step: completedStep,
      total: estimatedAmountToCharge.value,
    });
  }

  const basePaymentStep = dynamicSteps.value.find((s) => s.key === 'payment');
  const currentStepKey = props.currentStep.replace('shipping', 'delivery');
  const nextStepKey =
    dynamicSteps.value[dynamicSteps.value.findIndex((s) => s.key === currentStepKey) + 1].key;

  if (nextStepKey === 'payment' && basePaymentStep?.enabled) {
    paymentUnlocked.value = true;
  }

  let nextStep = !awaitingCustomer.value
    ? steps.value[nextStepKey]
    : Object.values(steps.value).find((step) => {
        const needPhysicalAddress = standardShipments.value.some((s) => s.needPhysicalAddress);
        if (cxMode.value && !cartCopiedFromOrder.value) {
          if (needPhysicalAddress) return step.enabled;
        }
        // somehow landed on the sign in page and ready for payment; maybe back navigation?
        // need to not break when submitting any step option
        if (basePaymentStep?.enabled) {
          const prepaymentSteps = Object.values(steps.value).filter((s) => s.enabled && s.complete);
          let nextKey =
            cxMode.value && needPhysicalAddress
              ? prepaymentSteps.find((s) => s.key === 'address')?.key
              : prepaymentSteps.pop()?.key;
          if (dynamicSteps.value.length === 1) {
            nextKey = 'payment';
            paymentUnlocked.value = true;
          }
          return step.key === nextKey;
        }
        return step.enabled && !step.complete;
      });

  if (!nextStep) throw new Error(`Cannot proceed to next step from ${props.currentStep}`);

  if (awaitingCustomer.value) {
    if (props.customer) {
      router.replace({ name: 'Checkout', params: { ...nextStep.route } });
      return;
    }
  }

  if (
    oneClickCheckoutEligible.value &&
    completedStep === 'address' &&
    nextStep.key === 'delivery'
  ) {
    if (steps.value.payment) {
      nextStep = steps.value.payment;
      paymentUnlocked.value = true;
      loadShippingOffers.execute();
    }
  }

  router.push({ name: 'Checkout', params: { ...nextStep.route } });
};

onMounted(async () => {
  watch(
    () => props.customer,
    async () => {
      try {
        paymentMethods.value = (await getSavedPaymentMethods())
          .filter((p) => {
            if (
              p.details &&
              'status' in p &&
              'cardType' in p.details &&
              p.details?.cardType === 'US Bank Account'
            ) {
              return p.status === USBankAccountStatus.VERIFIED;
            }
            return p;
          })
          .map((p) => ({
            ...p,
            type: p.type.replaceAll(' ', ''),
          }));
      } catch (e) {
        paymentMethods.value = [];
        console.error(e);
      }

      availablePaymentMethods.value = determineAvailablePaymentMethods();
    },
    { immediate: true },
  );
});

watch(
  () => dynamicSteps.value.length,
  (stepCount) => {
    if (stepCount === 1) paymentUnlocked.value = true;
    else if (cxMode.value) paymentUnlocked.value = false;
  },
);

const addressStepRef = ref<HTMLFormElement>();
const findAddressStep = () => {
  if (props.currentStep !== 'address') return;
  addressStepRef.value = document.querySelector<HTMLFormElement>('#address-step') ?? undefined;
};
let beforeUnloadInitialized = false;
onMounted(async () => {
  findAddressStep();
  const onBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!beforeUnloadInitialized) return undefined;
    event.preventDefault();
    // eslint-disable-next-line no-param-reassign, no-return-assign
    return (event.returnValue = ''); // chrome needs a return value set
  };
  useEventListener('beforeunload', onBeforeUnload, true);
  const stopWhenInitialized = watchEffect(() => {
    if (beforeUnloadInitialized) stopWhenInitialized();
    if (['shipping', 'payment'].includes(props.currentStep)) {
      beforeUnloadInitialized = true;
    }
  });
  const cleanupInputListener = useEventListener(addressStepRef, 'input', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.value !== '') {
      beforeUnloadInitialized = true;
      cleanupInputListener();
      stopWhenInitialized();
      addressStepRef.value = undefined;
    }
  });

  if (oneClickCheckoutEligible.value && steps.value.address?.complete) {
    await loadShippingOffers.execute();
  }
});

const stepTransitionsPending = reactive<Record<ExtendedStep['key'], boolean>>(
  dynamicSteps.value.reduce((acc, step) => ({ ...acc, [step.key]: false }), {} as any),
);

onMounted(() => {
  whenever(
    () => !Object.values(stepTransitionsPending).some(Boolean),
    () => {
      let query = `#${props.currentStep.replace('shipping', 'delivery')}-step`;

      if (oneClickCheckoutEligible.value && props.currentStep === 'payment') {
        query = '#delivery-summary';
      }

      root.value
        .querySelector(query)
        ?.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });

      findAddressStep();
    },
    { immediate: true },
  );
});

function handleDeliverySummaryEdit(
  route: { step: string; stepVariant?: string },
  setGiftMessage?: boolean,
) {
  if (setGiftMessage) {
    shippingOfferSets.value.forEach((_, index) => {
      shippingOfferSets.value[index].giftOptions.includeGiftMessage = true;
    });
  }

  router.push({ name: 'Checkout', params: { ...route } });
}
</script>

<template>
  <div>
    <transition name="fade">
      <SearchCustomerByEmail
        v-if="awaitingCustomer && cxMode"
        class="px-4 mb-5 md:px-10 md:mb-8"
        @step-complete="onStepComplete('signin')"
      />
    </transition>
    <transition name="fade">
      <CustomerEmailSummary
        v-if="!awaitingCustomer && cxMode"
        class="px-4 pb-5 mb-5 border-b-2 border-black border-solid md:px-10 md:pb-8 md:mb-8"
      />
    </transition>

    <div class="mx-4 md:mx-10">
      <h1 v-if="customer && !cxMode" class="text-xl leading-7 md:text-2xl">Welcome back!</h1>
      <InformationBanner
        v-if="specialDeliveryShipments.length && !awaitingCustomer"
        class="mb-6"
        variant="info"
      >
        <BaseBodyText>
          <b>This order contains special delivery items.</b>
          <div class="mt-2 mb-1">
            Special delivery items will be sent directly to their recipients. See item details in
            <strong>Items in Order > Special Delivery Items</strong>
          </div>
        </BaseBodyText>
      </InformationBanner>
      <InformationBanner
        v-if="containsGiftPurchase(lineItems) && needAddress(lineItems)"
        class="mb-6"
        variant="info"
      >
        <BaseBodyText class="font-semibold">
          Sending a gift? Double-check the shipping address to make sure it ships to the right
          place!
        </BaseBodyText>
      </InformationBanner>
    </div>

    <div
      class="divide-y-[12px] md:divide-y-2 divide-nuts-neutral-100 md:divide-neutral-300 divide-solid md:px-10"
    >
      <Step
        v-if="steps.address"
        v-model:transitionPending="stepTransitionsPending.address"
        class="relative pb-5 md:pb-8"
        :disabled="awaitingCustomer"
        :hideChangeLink="currentStep === steps.address.route.step || awaitingCustomer"
        :step="steps.address"
      >
        <Address
          v-if="steps.address.enabled && currentStep === steps.address.route.step"
          :id="`${steps.address.key}-step`"
          @step-complete="onStepComplete(steps.address.key)"
        />
        <AddressSummary
          v-else-if="
            steps.address.complete && currentStep !== steps.address.route.step && !awaitingCustomer
          "
          class="mt-5 md:mt-8"
          :id="`${steps.address.key}-summary`"
        />
      </Step>

      <Step
        v-if="steps.delivery"
        v-model:transitionPending="stepTransitionsPending.delivery"
        class="relative pb-5 md:pb-8"
        :disabled="awaitingCustomer"
        :hideChangeLink="
          currentStep === steps.delivery.route.step ||
          awaitingCustomer ||
          (oneClickCheckout && standardShipments.every((s) => !s.hasPhysical))
        "
        :step="steps.delivery"
      >
        <Delivery
          v-if="steps.delivery.enabled && currentStep === steps.delivery.route.step"
          v-model:lasershipAllowed="lasershipAllowed"
          v-model:ontracAllowed="ontracAllowed"
          v-model:regionalCarriersAllowed="regionalCarriersAllowed"
          :delivery
          :id="`${steps.delivery.key}-step`"
          :skipLoadingOffersOnMount="oneClickCheckoutEligible && shippingOfferSets.length > 0"
          @step-complete="onStepComplete(steps.delivery.key)"
        />
        <DeliverySummary
          v-else-if="
            (loadShippingOffers.isPending || steps.delivery.complete) &&
            currentStep !== steps.delivery.route.step &&
            !awaitingCustomer
          "
          :class="{ 'mt-5 md:mt-8': !oneClickCheckout }"
          :id="`${steps.delivery.key}-summary`"
          :isLoadingShippingOffers="loadShippingOffers.isPending"
          :oneClickCheckout
          @edit="handleDeliverySummaryEdit(steps.delivery.route, $event)"
        />
      </Step>

      <Step
        v-if="steps.payment"
        v-model:transitionPending="stepTransitionsPending.payment"
        :disabled="awaitingCustomer || disablePaymentStep"
        :step="steps.payment"
      >
        <Payment
          v-if="steps.payment.enabled"
          v-show="!disablePaymentStep"
          :availablePaymentMethods
          class="mt-5 md:mt-8"
          :disablePlaceOrderButton="loadShippingOffers.isPending || disablePaymentStep"
          :id="`${steps.payment.key}-step`"
          :paymentMethods
          @order-placed="onOrderPlaced"
        />
      </Step>
    </div>
  </div>
</template>
